// Flight and compiled-speed readers shared by posture and upkeep.
// The caller expresses the body's centre relative to its physical support floor.
export const FLYING_SPEED_TYPE_ID = 'F1D0A27A-43F1-4A4C-A5F8-ED9BD2D908D4';
export const HOVERING_SPEED_TYPE_ID = 'AE7D4EB3-DC36-BD47-978F-1DBCFE2B616A';
const GROUNDED_SUPPORT_TOLERANCE_FT = 2;

/** A preferred flying/hovering speed must be positive, and the feet above support. */
export function isAloftUnderOwnFlight(
    zCenter: number,
    physicsHeight: number,
    preferredSpeedType: unknown,
    flyingSpeed: number,
    hoveringSpeed: number,
): boolean {
    // Math.trunc mirrors check_and_apply_falling.php's (int) cast: a fractional speed is ZERO to the
    // falling engine, so a 0.5 ft flyer must not read as aloft here either.
    const preferred = String(preferredSpeedType ?? '').toUpperCase();
    const flying = preferred === FLYING_SPEED_TYPE_ID && Math.trunc(Number(flyingSpeed) || 0) > 0;
    const hovering = preferred === HOVERING_SPEED_TYPE_ID && Math.trunc(Number(hoveringSpeed) || 0) > 0;
    if (!flying && !hovering) {
        return false;
    }
    if (!Number.isFinite(zCenter) || !Number.isFinite(physicsHeight) || physicsHeight <= 0) {
        // Fail open: without trustworthy geometry we cannot claim the creature is airborne.
        return false;
    }
    return (zCenter - physicsHeight / 2) > GROUNDED_SUPPORT_TOLERANCE_FT;
}

/**
 * layer_value('speed_<kind>') twin for the computed cluster. compute_speeds keys
 * computed.creature.speeds WITHOUT the `speed_` prefix (speeds.flying, speeds.hovering) and each
 * slot is a bare number or a {total} wrapper — exactly the bridge build_item_arr.php's layer_value()
 * applies. Returns undefined — NOT 0 — when the cluster cannot answer: a computed zero is
 * authoritative (a curse or an anchor took the speed away) and must not be overwritten by the
 * authored layer, or the client would call a grounded creature a flyer while the server does not
 * (Codex review 2026-09-02). Lives here so the smoke can hold it to a real fixture shape.
 */
export function speedFeetFromCluster(speeds: unknown, variableName: string): number | undefined {
    if (!speeds || typeof speeds !== 'object' || Array.isArray(speeds)) {
        return undefined;
    }
    const slot = (speeds as Record<string, unknown>)[variableName.replace(/^speed_/, '')];
    const raw = (slot && typeof slot === 'object' && !Array.isArray(slot))
        ? (slot as Record<string, unknown>).total
        : slot;
    const value = Number(Array.isArray(raw) ? raw[0] : raw);
    return Number.isFinite(value) ? value : undefined;
}
